import { useCallback, useState } from 'react'
import { useCurrentAccount, useDAppKit } from '@mysten/dapp-kit-react'
import styled from 'styled-components'
import { usePostHog } from 'posthog-js/react'

import { Dialog } from 'components/Dialog/Dialog'
import { Typography } from 'components/Typography/Typography'
import Checkbox from 'components/Checkbox/Checkbox'
import { Button } from 'components/Button/Button'
import { Spinner } from 'components/Spinner/Spinner'
import { useTermsAcceptance } from './TermsAcceptanceContext'
import { clearSentrySession } from 'utils/sentry-utils'
import { ROUTES } from 'utils/route-constants'
import { useLocation } from 'react-router-dom'

const EXCLUDED_PATHS = [`/${ROUTES.TERMS}`, `/${ROUTES.PRIVACY_POLICY}`]

const Subtitle = styled(Typography)`
  margin-top: 16px;
  text-align: center;
`

const CheckboxRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 20px;
`

const CheckboxLabel = styled(Typography)`
  cursor: pointer;
  line-height: 1.5;
`

const StyledLink = styled.a`
  color: ${p => p.theme.colors.button.background};

  &:hover {
    text-decoration: underline;
  }
`

const ButtonRow = styled.div`
  display: flex;
  gap: 16px;
  margin-top: 24px;

  & > button {
    flex: 1;
  }
`

export const TermsAcceptance = () => {
  const currentAccount = useCurrentAccount()
  const { hasAcceptedTerms, setAcceptedTerms, isAcceptingTerms, isLoading } = useTermsAcceptance()
  const dAppKit = useDAppKit()
  const posthog = usePostHog()

  const [termsChecked, setTermsChecked] = useState(false)

  const { pathname } = useLocation()
  const normalizedPathname = pathname.replace(/\/+$/, '')
  const isExcludedPath = EXCLUDED_PATHS.includes(normalizedPathname)
  const isOpen = !!currentAccount && !isLoading && !hasAcceptedTerms && !isExcludedPath

  const handleDecline = useCallback(() => {
    if (isAcceptingTerms) {
      return
    }
    clearSentrySession()
    posthog?.reset()
    posthog?.reloadFeatureFlags()
    void dAppKit.disconnectWallet()
    setTermsChecked(false)
  }, [dAppKit, posthog, isAcceptingTerms])

  const handleAccept = useCallback(() => {
    setAcceptedTerms(true)
  }, [setAcceptedTerms])

  return (
    <Dialog title="Terms & Conditions" isOpen={isOpen} onClose={handleDecline}>
      <Subtitle variant="description" fontWeight="medium" color="secondary">
        To proceed, review and accept the following:
      </Subtitle>

      <CheckboxRow>
        <Checkbox checked={termsChecked} onChange={() => setTermsChecked(prev => !prev)} />
        <CheckboxLabel variant="description" fontWeight="medium">
          You acknowledge that you have read, understood, and agreed to the{' '}
          <StyledLink href={`/${ROUTES.TERMS}`} target="_blank" rel="noopener noreferrer">
            Terms of Use
          </StyledLink>{' '}
          and{' '}
          <StyledLink href={`/${ROUTES.PRIVACY_POLICY}`} target="_blank" rel="noopener noreferrer">
            Privacy Policy
          </StyledLink>
          .
        </CheckboxLabel>
      </CheckboxRow>
      <ButtonRow>
        <Button outlined disabled={isAcceptingTerms} onClick={handleDecline}>
          Decline
        </Button>
        <Button disabled={!termsChecked || isAcceptingTerms} onClick={handleAccept}>
          {isAcceptingTerms ? <Spinner /> : 'Accept'}
        </Button>
      </ButtonRow>
    </Dialog>
  )
}
